import { Directive, ElementRef, forwardRef, HostListener, Input, Renderer2 } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SimpleNumberValueFormatter } from '../helpers/number-formatter.helper';
import { ConvertISONumberToNormal, ConvertISONumberToNormalKeepSigns } from '../helpers/utils';
import { convertISONumberToNormal, convertISONumberToNormalKeepSigns, formatSimpleNumber } from '../helpers/v2';

@Directive({
  selector: '[appOnlyNumber]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OnlyNumberDirective),
      multi: true,
    },
  ],
})
export class OnlyNumberDirective implements ControlValueAccessor {
  @Input() appOnlyNumber: boolean | '' = false;
  @Input() isFx: boolean | '' = false;
  @Input() allowedSign: string | 'none' = '';
  @Input() keepSignAndZero: boolean;
  @Input() allowZero = false;
  private onChange!: (val: string | number) => void;
  private onTouched!: () => void;
  private value!: string | number;

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
  ) {}

  @HostListener('input', ['$event.target.value'])
  onInputChange(value: string) {
    const filteredValue: string | number = filterValue(value, this.allowedSign, this.allowZero);
    this.updateTextInput(filteredValue, this.value !== filteredValue);
  }

  @HostListener('blur')
  onBlur() {
    if (this.appOnlyNumber) {
      this.onValueFormating();
    }
  }
  @HostListener('focus')
  onFocus() {
    if (!this.value) {
      return;
    }
    if (this.keepSignAndZero) {
      this.updateTextInput(convertISONumberToNormalKeepSigns(this.value, this.keepSignAndZero), false);
    } else {
      this.updateTextInput(isNaN(convertISONumberToNormal(this.value as string)) ? this.value : convertISONumberToNormal(this.value as string), false);
    }
  }

  private updateTextInput(value: string | number, propagateChange: boolean) {
    this.renderer.setProperty(this.elementRef.nativeElement, 'value', value);
    if (propagateChange) {
      this.onChange(value);
    }
    this.value = value;
  }

  onValueFormating() {
    if (!this.value) {
      return;
    }
    const decimals = this.isFx ? 4 : 2;
    const formatted = this.allowZero && +this.value === 0 ? (0).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) : formatSimpleNumber(this.value, decimals)!;
    this.updateTextInput(formatted, false);
  }
  // ControlValueAccessor Interface
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
  }

  writeValue(value: any): void {
    const decimals = this.isFx ? 4 : 2;
    const isZero = this.allowZero && value !== null && value !== undefined && value !== '' && +value === 0;
    if (value || isZero) {
      value = this.appOnlyNumber ? (isZero ? (0).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) : formatSimpleNumber(value, decimals)) : String(value);
    } else {
      value = '';
    }
    this.updateTextInput(value, false);
  }
}

function filterValue(value: any, allowedSign = '', allowZero = false): string | number {
  // If allowedSign is 'none', allow only numbers 0-9
  if (allowedSign === 'none') {
    const filtered = value.replace(/[^0-9]/g, '');
    return allowZero && filtered === '' ? '0' : filtered;
  }

  // When allowZero is true, use stricter regex (no + or - signs)
  const regex = allowedSign
    ? new RegExp(`[^0-9\\${allowedSign}]`, 'g')
    : allowZero
      ? /[^0-9.]/g
      : /[^0-9+\-.]/g;

  value = value.replace(regex, '');

  // Ensure the allowed sign appears only once in the entire value
  if (allowedSign && value.includes(allowedSign)) {
    const firstOccurrenceIndex = value.indexOf(allowedSign);
    value =
      value.slice(0, firstOccurrenceIndex + 1) + // Keep the sign and numbers up to its position
      value.slice(firstOccurrenceIndex + 1).replace(new RegExp(`\\${allowedSign}`, 'g'), ''); // Remove extra occurrences
  }

  // When allowZero is true, handle decimal points and leading zeros more strictly
  if (allowZero) {
    const decimalIndex = value.indexOf('.');
    if (decimalIndex !== -1) {
      value =
        value.slice(0, decimalIndex + 1) +
        value.slice(decimalIndex + 1).replace(/\./g, '');
    }
    // Remove leading zeros except for '0' itself or '0.'
    if (value.startsWith('0') && !value.startsWith('0.')) {
      value = value.replace(/^0+/, '');
    }
    // Return '0' for empty values when allowZero is true
    return value === '' ? '0' : value;
  }

  return value;
}
